
import { ScriptureReference, CommonObjection } from "@/components/InterfaithDialogue/types";

/**
 * Clean section content to remove duplications and fix formatting
 */
export function cleanSectionContent(content: string): string {
  if (!content) return '';
  
  return content
    // Remove any markdown headers including numbered ones
    .replace(/^#+\s+.*$|^\d+\.\s+.*$/gm, '')
    // Remove section labels that might be embedded
    .replace(/^(Direct Answer|Biblical Foundation|Comparison of Beliefs|Refutation|Theological Reasoning|Final Appeal):\s*/gim, '')
    // Fix accidentally merged words
    .replace(/([a-z])([A-Z][a-z]+)/g, '$1 $2')
    // Remove markdown formatting
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    // Fix excessive newlines and ensure proper spacing
    .replace(/\n{3,}/g, '\n\n')
    // Remove extra spaces
    .replace(/\s{2,}/g, ' ')
    // Trim whitespace
    .trim();
}

/**
 * Remove duplicate paragraphs from a text section
 */
export function removeDuplicateParagraphs(content: string): string {
  if (!content) return '';
  
  // Split into paragraphs
  const paragraphs = content.split(/\n\s*\n/);
  const uniqueParagraphs: string[] = [];
  const seen = new Set<string>();
  
  for (const paragraph of paragraphs) {
    // Skip empty paragraphs
    const trimmed = paragraph.trim();
    if (!trimmed) continue;
    
    // Create a normalized version to check for duplicates
    const normalized = trimmed.toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ')    // Normalize whitespace
      .trim();
    
    // Only add paragraphs we haven't seen before
    if (normalized && normalized.length > 15 && !seen.has(normalized)) {
      seen.add(normalized);
      uniqueParagraphs.push(trimmed);
    } else if (normalized.length <= 15) {
      // Short paragraphs might be headings, keep those without deduplication
      uniqueParagraphs.push(trimmed);
    }
  }
  
  return uniqueParagraphs.join('\n\n');
}

/**
 * Deduplicate scripture references by reference
 */
export function deduplicateScriptureReferences(references: ScriptureReference[]): ScriptureReference[] {
  if (!Array.isArray(references)) return [];
  
  const uniqueReferences = new Map<string, ScriptureReference>();
  
  // Clean and normalize each reference
  for (const ref of references) {
    if (!ref || typeof ref !== 'object' || !ref.reference || !ref.text) continue;
    
    // Normalize the reference for comparison (lowercase, no spaces)
    const normalized = ref.reference.toLowerCase().replace(/\s+/g, '');
    
    // Only add references we haven't seen before
    if (!uniqueReferences.has(normalized)) {
      uniqueReferences.set(normalized, {
        reference: ref.reference.trim(),
        text: ref.text.trim().replace(/^["']|["']$/g, ''), // Remove quotes if they exist
        explanation: ref.explanation || '' // Ensure explanation exists, even if empty
      });
    }
  }
  
  return Array.from(uniqueReferences.values());
}

/**
 * Deduplicate objections by their content
 */
export function deduplicateObjections(objections: CommonObjection[]): CommonObjection[] {
  if (!Array.isArray(objections)) return [];
  
  const uniqueObjections = new Map<string, CommonObjection>();
  
  // Clean and normalize each objection
  for (const obj of objections) {
    if (!obj || typeof obj !== 'object' || !obj.objection || !obj.rebuttal) continue;
    
    // Normalize the objection for comparison (lowercase, no extra spaces)
    const normalized = obj.objection.toLowerCase().replace(/\s+/g, ' ').trim();
    
    // Only add objections we haven't seen before
    if (!uniqueObjections.has(normalized)) {
      uniqueObjections.set(normalized, {
        objection: obj.objection.trim(),
        rebuttal: obj.rebuttal.trim()
      });
    }
  }
  
  return Array.from(uniqueObjections.values());
}
